import api from 'src/utils/ajax';

import { Identifier } from '../../types/utilityTypes';

import {
  ScheduledAppointment,
  UpdateScheduledAppointmentParams,
} from './_types';
import {
  denormalizeScheduledAppointment,
  denormalizeUpdateParams,
  normalizeScheduledAppointment,
} from './normalizers';

/**
 * Fetch a scheduled appointment by ID
 */
export const fetchScheduledAppointment = async (
  id: Identifier
): Promise<ScheduledAppointment> => {
  const response = await api.get(`/api/v4/fbm/scheduled_appointments/${id}`);
  return normalizeScheduledAppointment(response.data);
};

/**
 * Create a new scheduled appointment (recurring series)
 */
export const createScheduledAppointment = async (
  scheduledAppointment: Partial<ScheduledAppointment>
): Promise<ScheduledAppointment> => {
  const response = await api.post(`/api/v4/fbm/scheduled_appointments`, {
    scheduled_appointment: denormalizeScheduledAppointment(scheduledAppointment),
  });
  return normalizeScheduledAppointment(response.data);
};

/**
 * Update a scheduled appointment (recurring series)
 * This updates all future appointments in the series
 * Matches v2 modal-new-appointment.js saveScheduledAppointment flow
 *
 * When called, the Rails controller:
 * 1. Updates the scheduled_appointment record
 * 2. Calls update_existing_appointments() which updates ALL future appointments
 */
export const updateScheduledAppointment = async (
  params: UpdateScheduledAppointmentParams
): Promise<ScheduledAppointment> => {
  const response = await api.put(
    `/api/v4/fbm/scheduled_appointments/${params.scheduledAppointmentId}`,
    { scheduled_appointment: denormalizeUpdateParams(params) }
  );
  return normalizeScheduledAppointment(response.data);
};

/**
 * Delete/cancel a scheduled appointment series
 * This cancels all future appointments from the given date
 *
 * @param id - The scheduled appointment ID
 * @param date - The date from which to cancel (YYYY-MM-DD format)
 * @param disableEmail - Whether to skip sending cancellation emails
 */
export const deleteScheduledAppointment = async (
  id: Identifier,
  date?: string,
  disableEmail?: boolean
): Promise<ScheduledAppointment> => {
  const response = await api.delete(
    `/api/v4/fbm/scheduled_appointments/${id}`,
    {
      params: {
        date,
        disable_email: disableEmail,
      },
    }
  );
  return normalizeScheduledAppointment(response.data);
};

// Re-export types for convenience
export type { ScheduledAppointment, UpdateScheduledAppointmentParams };

